import { useEffect, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ExploreIcon from '@mui/icons-material/Explore';
import { EVENTS, Joyride } from 'react-joyride';
import type { EventHandler, Step } from 'react-joyride';
import { TourTooltip } from './TourTooltip';
import { filterValidSteps, findVisibleElement, useJoyrideOptions } from './tourUtils';

interface FirstEpisodeTutorialProps {
  open: boolean;
  onComplete: () => void;
  onSkip: () => void;
  playerUrl: string | null;
}

/** Delay (ms) after `open` flips true so the add-episode dialog finishes
 * closing before the celebration anchors to the episode row. */
const ANCHOR_MOUNT_DELAY = 300;

const EPISODE_ANCHOR = '[data-tour="first-added-episode"]';

export const FirstEpisodeTutorial = ({ open, onComplete, onSkip, playerUrl }: FirstEpisodeTutorialProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation('common');
  const joyrideOptions = useJoyrideOptions();
  const [steps, setSteps] = useState<Step[]>([]);

  const handleStartLearning = () => {
    if (playerUrl) {
      // Parent onComplete also navigates to the same URL — idempotent.
      onComplete();
      navigate(playerUrl);
    }
  };

  const handleExploreLater = () => {
    onSkip();
  };

  useEffect(() => {
    if (!open) {
      setSteps([]);
      return;
    }

    const timer = window.setTimeout(() => {
      const content = (
        <Box display="flex" flexDirection="column" gap={2} textAlign="center" pt={1}>
          <Typography variant="body2" color="text.secondary">
            {t('firstEpisode.description')}
          </Typography>

          <Button
            variant="contained"
            startIcon={<PlayArrowIcon />}
            onClick={handleStartLearning}
            disabled={!playerUrl}
          >
            {t('firstEpisode.startLearning')}
          </Button>

          <Button
            variant="outlined"
            startIcon={<ExploreIcon />}
            onClick={handleExploreLater}
          >
            {t('firstEpisode.exploreLater')}
          </Button>

          <Typography variant="caption" color="text.secondary">
            {t('firstEpisode.helperText')}
          </Typography>
        </Box>
      );

      const hasAnchor = findVisibleElement(EPISODE_ANCHOR) !== null;
      const celebrationStep: Step = {
        target: hasAnchor ? EPISODE_ANCHOR : 'body',
        ...(hasAnchor ? {} : { placement: 'center' as const }),
        title: `🎉 ${t('firstEpisode.title')}`,
        content,
        // Action buttons live inside the content; hide the default footer.
        data: { hideFooter: true },
      };

      setSteps(filterValidSteps([celebrationStep]));
    }, ANCHOR_MOUNT_DELAY);

    return () => window.clearTimeout(timer);
    // handleStartLearning/handleExploreLater are stable per render via closure;
    // rebuilding on playerUrl keeps the Start Learning button state correct.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, playerUrl, t]);

  // The footer is hidden, so the tour can only end on its own via overlay
  // click or ESC — both are dismissals (session dismiss only, like the old
  // Dialog's non-action close). The content buttons call onComplete/onSkip
  // directly and unmount the tour before any joyride event fires.
  const handleEvent: EventHandler = (data) => {
    if (data.type === EVENTS.TOUR_END) {
      onSkip();
    }
  };

  if (!open || steps.length === 0) {
    return null;
  }

  return (
    <Joyride
      run
      steps={steps}
      tooltipComponent={TourTooltip}
      onEvent={handleEvent}
      options={{
        ...joyrideOptions,
        // Overlay click / ESC end the single-step tour -> dismiss via onSkip
        overlayClickAction: 'next',
        dismissKeyAction: 'next',
      }}
    />
  );
};
